import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useReplyTrend } from '@/hooks/ml';
import { useDrillDown } from './DrillDownContext';

export const ReplyTrendCard: React.FC = () => {
  // 0 = all history since 2021-01-01 (default — full legacy window).
  const [days, setDays] = useState(0);
  const { drillCboId } = useDrillDown();
  const { data, isLoading, isError } = useReplyTrend(days, drillCboId);

  // Memoize the chart data so the array reference is stable across renders
  // (otherwise Recharts re-mounts/animates on every re-render).
  const series = useMemo(() => {
    if (!data?.points?.length) return [];
    return data.points.map((p) => ({
      date: format(parseISO(p.send_date), 'MMM d'),
      sends: p.sends,
      engagements: p.engagements,
      replies: p.replies,
      clicks: p.clicks,
    }));
  }, [data]);

  // Headline numbers for the title strip
  const totals = useMemo(() => {
    if (!data?.points?.length) return null;
    const sends = data.points.reduce((a, p) => a + p.sends, 0);
    const replies = data.points.reduce((a, p) => a + p.replies, 0);
    const clicks = data.points.reduce((a, p) => a + p.clicks, 0);
    const engagements = data.points.reduce((a, p) => a + p.engagements, 0);
    return {
      sends,
      replies,
      clicks,
      engagements,
      rate: sends > 0 ? (engagements / sends) * 100 : 0,
    };
  }, [data]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-sm font-medium">Engagement (reply or click) over time</CardTitle>
          {totals && (
            <div className="text-xs text-gray-500 mt-0.5">
              {totals.sends.toLocaleString()} sends · {totals.engagements.toLocaleString()} engaged
              {' ('}
              {totals.replies.toLocaleString()} replies + {totals.clicks.toLocaleString()} clicks)
              {' · '}
              <span className="font-medium text-gray-700 tabular-nums">{totals.rate.toFixed(2)}%</span> overall
            </div>
          )}
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
        >
          <option value={0}>All since 2021</option>
          <option value={1095}>Last 3 years</option>
          <option value={730}>Last 2 years</option>
          <option value={365}>Last 12 months</option>
          <option value={180}>Last 6 months</option>
          <option value={90}>Last 90 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-500">Loading…</div>
        ) : isError || !series.length ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-500">
            No data for the selected window.
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tick={{ fontSize: 11 }}
                  minTickGap={24}
                />
                <YAxis
                  stroke="#6b7280"
                  tick={{ fontSize: 11 }}
                  domain={[0, 'dataMax']}
                  allowDecimals={false}
                  width={48}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'sends') return [Number(value).toLocaleString(), 'Sends'];
                    if (name === 'engagements') return [Number(value).toLocaleString(), 'Engaged (reply + click)'];
                    if (name === 'replies') return [Number(value).toLocaleString(), 'Replies only'];
                    if (name === 'clicks') return [Number(value).toLocaleString(), 'Clicks only'];
                    return [value, name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="sends"
                  stroke="#3b82f6"
                  name="Sends"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="engagements"
                  stroke="#10b981"
                  name="Engaged (reply + click)"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="clicks"
                  stroke="#8b5cf6"
                  name="Clicks only"
                  dot={false}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="replies"
                  stroke="#f59e0b"
                  name="Replies only"
                  dot={false}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
